import type { Permission } from '@fieldops/shared'
import type { Audience, ReleaseItem, ReleaseNote } from './types.js'

/**
 * What changed, newest first. Decision 115.
 *
 * **Add a note with every release that changes what somebody sees.** The
 * notes ship in the bundle, so a person reads them the first time they open
 * the version that has them; `guide.seeReleases` remembers how far they got.
 * One note per deploy, its id the deploy's date. Write for the person using
 * the screen: what is different and why it helps, never how it was built.
 * An item with a `permission` or an `audience` is shown only to those it
 * applies to, so a technician is not told about Billing.
 */
export const RELEASES: ReleaseNote[] = [
  {
    id: '2026-11-02',
    title: 'See anyone’s day without leaving the screen',
    summary: 'Right-click a person or a booking for their day on a map, with why each stop is there, their free time, and waiting work that could fit.',
    items: [
      {
        kind: 'new',
        title: 'View day',
        body: 'Right-click a name or a booking on the Board, click the map button beside a name, or press V on a booking. Their day opens over the Board: the stops in order on a map, why each one is there, the free time between them, and the rest of the week a click away. Close it and you are back where you were.',
        article: 'board-day-peek',
        to: '/board',
        permission: 'SCHEDULE_VIEW',
        audience: 'desk',
      },
      {
        kind: 'new',
        title: 'Who is passing near a job',
        body: 'Open a job in the Board’s waiting list, or on the Booking desk, to see who will be close to it and whether it fits their day. View day opens their day with the job ready to pencil in.',
        article: 'board-day-peek',
        to: '/board?mode=desk',
        permission: 'SCHEDULE_EDIT',
        audience: 'desk',
      },
    ],
  },
  {
    id: '2026-11-01',
    title: 'Eco on a full page, shorter answers, and thumbs up or down',
    summary: 'Open Eco as a page, see the main things first with the rest a tap away, tell Eco how it did, and send what it cannot do yet to the team.',
    items: [
      {
        kind: 'new',
        title: 'Eco on a full page',
        body: 'Press the expand button at the top of Eco for a page of its own, with your conversations down the side. The button beside it puts Eco back in the side panel, on the same conversation.',
        article: 'ask-eco',
        to: '/eco',
        permission: 'ASSISTANT_USE',
        audience: 'desk',
      },
      {
        kind: 'improved',
        title: 'The main things first',
        body: 'Long answers, long lists and busy days now show the first few and a button for the rest, and the steps Eco took fold into one line you can open.',
        article: 'ask-eco',
        permission: 'ASSISTANT_USE',
        audience: 'all',
      },
      {
        kind: 'new',
        title: 'Thumbs up or down, and send it to the team',
        body: 'Rate any answer. A thumbs-down asks what was wrong and offers to try again. When Eco cannot do something, or something breaks, it drafts a feature request or a bug report for you to send — you follow it in Help → Your reports.',
        article: 'ask-eco',
        permission: 'ASSISTANT_USE',
        audience: 'all',
      },
    ],
  },
  {
    id: '2026-10-31',
    title: 'Make, move or skip a visit right from the agreement',
    summary: 'Right-click any booking or visit on an agreement to make its job, push it back or skip it, and see an agreement by service.',
    items: [
      {
        kind: 'new',
        title: 'A menu on every booking and visit',
        body: 'On an agreement, right-click a booking or a visit, or click one with no job yet. Make its job and open it, add a note, push it back a week or a month, skip it or split it off. Only that visit moves: the rest of the agreement keeps its dates.',
        article: 'agreement-visit-menu',
        to: '/agreements',
        permission: 'CLIENT_EDIT',
        audience: 'desk',
      },
      {
        kind: 'new',
        title: 'An agreement by service',
        body: 'Switch an agreement to By service to see each service with the sites it is done at. The same service on a different cadence, or with different instructions, shows on its own.',
        article: 'agreements',
        permission: 'CLIENT_VIEW',
        audience: 'desk',
      },
      {
        kind: 'new',
        title: 'One service at many sites',
        body: 'Add a service to several sites at once, add it to more sites later, or change it at every site in one go. Each site keeps its own first visit and price. Edit service also has instructions that go into every job it makes.',
        article: 'agreements',
        permission: 'CLIENT_EDIT',
        audience: 'desk',
      },
    ],
  },
  {
    id: '2026-10-30',
    title: 'Service agreements by the month, auto-schedule, and a person’s day on the map',
    summary: 'See every agreement’s jobs on a calendar and pencil in the month’s in one go, and see each person’s path on the map.',
    items: [
      {
        kind: 'new',
        title: 'A person’s day on the map',
        body: 'The map draws each person’s day as a path. Click a person to see their day or their whole week, with the work waiting to be booked nearby, by its stage on the Booking desk.',
        article: 'board-map',
        to: '/board?view=map',
        permission: 'SCHEDULE_VIEW',
        audience: 'desk',
      },
      {
        kind: 'new',
        title: 'Could fit',
        body: 'With a person open, the map lists the waiting jobs near their path that fit a gap in their day, least extra driving first. Pencil one in from there.',
        article: 'board-map',
        permission: 'SCHEDULE_EDIT',
        audience: 'desk',
      },
      {
        kind: 'new',
        title: 'Agreements on a calendar',
        body: 'Click Calendar on Agreements to see what is due this month and next: how many jobs land, how many are booked, and each agreement’s share.',
        article: 'agreements',
        to: '/agreements?view=calendar',
        permission: 'CLIENT_VIEW',
        audience: 'desk',
      },
      {
        kind: 'new',
        title: 'Auto-schedule',
        body: 'Pencil in a month’s agreement jobs, or one agreement’s, with whoever went last time on a day near when each is due. Swap the person for one job or all of them, and check every day before you book.',
        article: 'agreements',
        permission: 'SCHEDULE_EDIT',
        audience: 'desk',
      },
    ],
  },
  {
    id: '2026-10-29',
    title: 'Billing in one place, a clearer Booking desk, and help on every screen',
    summary:
      'Open quotes, work to invoice and money owed on one screen. The Booking desk shows the whole pile at once. Duplicate clients can be merged. And help is one key away.',
    items: [
      {
        kind: 'new',
        title: 'Meet Eco, the assistant',
        body: 'Click the frog in the rail, or press ⌘J, and ask about today, yesterday, a customer’s history or an agreement. Eco can book jobs, write emails and texts, and change agreements — it shows you each one first, and you can change it before you approve.',
        article: 'ask-eco',
        permission: 'ASSISTANT_USE',
        audience: 'all',
      },
      {
        kind: 'new',
        title: 'Use EcoPest from Claude, ChatGPT, Codex or Cursor',
        body: 'Connect the AI app you already use under Settings → AI apps. It works as you, and anything it drafts happens only when it confirms after asking you.',
        article: 'connect-an-ai-app',
        to: '/settings?tab=ai',
        permission: 'ASSISTANT_USE',
        audience: 'desk',
      },
      {
        kind: 'new',
        title: 'Help on every screen',
        body: 'Press ? anywhere, or the Help button above your initials, for the guides to the screen you are on. Search every guide, take the tour again, and catch up on what changed — like this.',
        shot: 'help-panel',
        article: 'help-and-the-tour',
        to: '/help',
        audience: 'desk',
      },
      {
        kind: 'new',
        title: 'Report a bug from any screen',
        body: 'Something not working? Open Help and choose Report a bug. Snip the problem or take the whole screen, draw on it if you like, and say what happened. Help → Bug reports shows where each one is, and asks you to check the fix.',
        article: 'report-a-bug',
        to: '/help/bugs',
        audience: 'all',
      },
      {
        kind: 'new',
        title: 'Help, the tour and what’s new on your phone',
        shot: 'field-help',
        body: 'Open Me and look under Help for the guides, a short tour of the app, and what changed in each version.',
        article: 'help-and-the-tour',
        to: '/me',
        audience: 'field',
      },
      {
        kind: 'new',
        title: 'Billing: open quotes, jobs to invoice and money owed',
        shot: 'billing-pipeline',
        body: 'One screen follows the money from the first quote to the last payment. Each step shows how many jobs and how much, and every row ends in its next step: price it, send it, follow it up, invoice it, or record the payment.',
        article: 'billing-overview',
        to: '/billing',
        permission: 'INVOICE_VIEW',
        audience: 'desk',
      },
      {
        kind: 'improved',
        title: 'The Booking desk shows the whole pile',
        shot: 'board-desk',
        body: 'Every unbooked job by stage and by the kind of customer — contract, regular, returning or new — in one grid. Make your own lists like “Chase up”, preview a job beside the list, and send follow-ups to one customer or many.',
        article: 'booking-desk',
        to: '/board?mode=desk',
        permission: 'SCHEDULE_EDIT',
        audience: 'desk',
      },
      {
        kind: 'new',
        title: 'Find and merge duplicate clients',
        shot: 'clients-merge-window',
        body: 'EcoPest finds clients that share an email, a phone number or a name. Review them side by side, keep the record linked to Xero, and undo a merge if it was a mistake.',
        article: 'merge-duplicates',
        to: '/clients/duplicates',
        permission: 'CLIENT_EDIT',
        audience: 'desk',
      },
      {
        kind: 'improved',
        title: 'Filter clients by people, companies and repeat work',
        shot: 'clients-list',
        body: 'The Clients list now separates people from companies and repeat clients from one-offs, with more filters for the last job, money owed and Xero.',
        article: 'clients-overview',
        to: '/clients',
        permission: 'CLIENT_VIEW',
        audience: 'desk',
      },
      {
        kind: 'improved',
        title: 'Agreement visits close together become one booking',
        shot: 'clients-agreement',
        body: 'Services on an agreement that fall within a couple of weeks of each other at the same site are booked as one visit. The agreement page shows them, and you can split one off before it is made.',
        article: 'agreements',
        to: '/agreements',
        permission: 'CLIENT_VIEW',
        audience: 'desk',
      },
      {
        kind: 'improved',
        title: 'Confirm a booking from its checklist',
        shot: 'board-quick-menu',
        body: 'On the Board, a booking’s quick menu now confirms it with a tick on the Confirmed step, and unticking it unlocks the booking to move.',
        article: 'board-pencilled-confirmed',
        to: '/board',
        permission: 'SCHEDULE_EDIT',
        audience: 'desk',
      },
      {
        kind: 'improved',
        title: 'Photos and PDFs open where you are',
        body: 'Photos on a job, in a report, in an email or on a proposal open over the screen you are on. Swipe or use the arrows to go through them, pinch or click to look closer, and press Esc to carry on where you were. Quotes, invoices and the copies customers got open the same way, with Download and Open in new tab still there.',
        article: 'job-notes-files',
        audience: 'all',
      },
      {
        kind: 'fixed',
        title: 'A dropped connection keeps your screen',
        body: 'If the internet drops, what is on screen stays put and refreshes by itself when the connection is back, instead of going blank.',
        article: 'connection-and-updates',
      },
    ],
  },
  {
    id: '2026-10-28',
    title: 'A new Board, the Booking desk, and every message shown before it goes',
    summary:
      'The Board is redrawn so a booking’s state reads at a glance. The Booking desk works through jobs to book. And every email or text is shown before it goes.',
    items: [
      {
        kind: 'new',
        title: 'A booking’s state reads at a glance',
        shot: 'board-timeline',
        body: 'Pencilled in is a dashed outline, confirmed is green, under way is dark and a problem has a red bar. Needs you lists what the board is waiting on.',
        article: 'board-pencilled-confirmed',
        to: '/board',
        permission: 'SCHEDULE_VIEW',
        audience: 'desk',
      },
      {
        kind: 'new',
        title: 'The Booking desk and pick-a-time links',
        shot: 'board-session',
        body: 'Work through the jobs that need booking one at a time, or text the customer a link to pick a time that suits them.',
        article: 'booking-desk',
        to: '/board?mode=desk',
        permission: 'SCHEDULE_EDIT',
        audience: 'desk',
      },
      {
        kind: 'new',
        title: 'Every message shown before it goes',
        shot: 'messages-compose',
        body: 'An email or a text opens in a window you can read and change before sending, and afterwards it says whether it went.',
        article: 'sending-messages',
      },
      {
        kind: 'improved',
        title: 'The EcoPest look on every screen',
        body: 'Every screen now shares the Board’s look: the dark bar along the top, one green button for the thing to do, and the same words for the same states.',
        audience: 'desk',
      },
    ],
  },
]

export const LATEST_RELEASE_ID = RELEASES[0]?.id ?? null

interface Reader {
  can: (...required: Permission[]) => boolean
  field: boolean
}

function applies(item: ReleaseItem, reader: Reader): boolean {
  if (item.permission && !reader.can(item.permission)) return false
  const audience: Audience = item.audience ?? 'all'
  if (audience === 'all') return true
  return reader.field ? audience === 'field' : audience === 'desk'
}

/** The notes this person would be shown, with only the items that apply to them. */
export function releasesFor(reader: Reader, releases: ReleaseNote[] = RELEASES): ReleaseNote[] {
  return releases
    .map((release) => ({ ...release, items: release.items.filter((item) => applies(item, reader)) }))
    .filter((release) => release.items.length > 0)
}

/**
 * The notes newer than the one they last read. Somebody who has never been
 * marked as reading any gets the newest only: the backlog from before they
 * started is history, not news.
 */
export function unseenReleases(
  seenThrough: string | null,
  reader: Reader,
  releases: ReleaseNote[] = RELEASES,
): ReleaseNote[] {
  const mine = releasesFor(reader, releases)
  if (!seenThrough) return mine.slice(0, 1)
  return mine.filter((release) => release.id > seenThrough)
}

export const RELEASE_KIND_LABEL = { new: 'New', improved: 'Better', fixed: 'Fixed' } as const

/** "29 October 2026", from an id that starts with its date. */
export function releaseDate(id: string): string {
  const [year, month, day] = id.slice(0, 10).split('-').map(Number)
  if (!year || !month || !day) return id
  return new Date(year, month - 1, day).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}
